import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Divider,
  GlobalStyles,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Money, crInstant, crDateOf, formatCRC } from "@despejo/domain";
import { buildFilingPackage, filingChecklist, type BuiltFilingPackage, type FilingChecklistItem, type DiffCause, type UserExplanation } from "@despejo/tax-engine";
import { Icon, StatusChip, formatDateEs, type IconName, type Tone } from "@despejo/ui";
import { periodText } from "../lib/dashboard.js";
import { parseAmount } from "../lib/csv.js";
import { can, filingKeys, useFilingData, useFilingHistory, useMyRole, usePackageComments, type FilingHistoryRow } from "../lib/filingData.js";
import { must, supabase } from "../lib/supabase.js";
import { uploadDocument } from "../lib/upload.js";
import type { ObligationRow } from "../lib/types.js";

export const FIGURE_LABEL: Record<string, string> = {
  salesExempt: "Ventas exentas",
  salesExport: "Exportaciones",
  salesNonSubject: "Ventas no sujetas",
  totalDebit: "Débito fiscal (IVA de ventas)",
  creditFull: "Crédito pleno",
  creditProrrata: "Crédito por prorrata",
  creditNotAllowed: "IVA no acreditable",
  totalCredit: "Crédito fiscal aplicable",
  cardRetentions: "Retenciones de tarjeta",
  priorCreditBalance: "Saldo a favor anterior",
  payable: "Impuesto por pagar",
  creditCarryForward: "Saldo a favor",
  amountDeclared: "Monto declarado",
};

const CAUSE_LABEL: Record<DiffCause, string> = {
  MISSING_INVOICE: "Factura que falta en Despejo",
  UNACCEPTED_INVOICE: "Factura sin aceptación en plazo",
  WRONG_CABYS: "CABYS o tarifa incorrecta en el borrador",
  PERSONAL_PURCHASE: "Compra personal",
  TIMING: "Diferencia de periodo",
  MANUAL_ADJUSTMENT: "Ajuste manual",
  ROUNDING: "Redondeo",
  OTHER: "Otra causa",
};

const STATUS_UI: Record<FilingChecklistItem["status"], { icon: IconName; tone: Tone; label: string }> = {
  OK: { icon: "check_circle", tone: "success", label: "Cumple" },
  WARNING: { icon: "help", tone: "warning", label: "Revisar" },
  BLOCKING: { icon: "error", tone: "error", label: "Bloquea" },
};

const money = (v: string | null | undefined) => (v === null || v === undefined ? "—" : formatCRC(Money.of(v)));
const shortHash = (h: string | null | undefined) => (h ? `${h.slice(0, 12)}…` : "—");

// ------------------------------------------------------------ printable body

function Checklist({ items }: { items: FilingChecklistItem[] }) {
  return (
    <List dense disablePadding>
      {items.map((c) => (
        <ListItem key={c.key} disableGutters alignItems="flex-start">
          <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
            <Icon name={STATUS_UI[c.status].icon} size={20} fill={c.status !== "OK"} label={STATUS_UI[c.status].label} />
          </ListItemIcon>
          <ListItemText
            primary={c.label}
            secondary={
              c.issues.length ? (
                <Box component="span" sx={{ display: "block" }}>
                  {c.issues.map((i, n) => (
                    <Box component="span" key={n} sx={{ display: "block" }}>
                      {i.severity === "BLOCKING" ? "Bloquea: " : "Aviso: "}
                      {i.message}
                    </Box>
                  ))}
                </Box>
              ) : (
                "Sin observaciones"
              )
            }
          />
        </ListItem>
      ))}
    </List>
  );
}

function PackageBody({ built, row, savedSha, approved, extra }: { built: BuiltFilingPackage; row: ObligationRow; savedSha: string | null; approved: string | null; extra?: ReactNode }) {
  const p = built.pkg;
  const items = filingChecklist(p.validation);
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" component="h2">
          Paquete de declaración — formulario 150
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {row.taxpayer_name} · {periodText(row.period_key)} · vence {formatDateEs(row.due_date)} · evaluado el {formatDateEs(p.asOf)}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        {p.validation.ok ? (
          <StatusChip tone="success" icon="task_alt" label="Sin bloqueos: listo para presentar" />
        ) : (
          <StatusChip tone="error" icon="error" label={`${p.validation.blocking.length} bloqueo(s) por resolver`} />
        )}
        {p.validation.warnings.length > 0 && <StatusChip tone="warning" icon="help" label={`${p.validation.warnings.length} aviso(s)`} />}
        {approved && <StatusChip tone="success" icon="verified" label={approved} />}
      </Stack>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Lista de verificación
        </Typography>
        <Checklist items={items} />
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Cifras del formulario 150
        </Typography>
        <Table size="small">
          <TableBody>
            {Object.entries(p.calculation.debitByRate).map(([rate, v]) => (
              <TableRow key={rate}>
                <TableCell>Ventas gravadas al {Money.of(rate).times(100).amount.toFixed()}% (base · IVA)</TableCell>
                <TableCell align="right">
                  {money(v.base)} · {money(v.iva)}
                </TableCell>
              </TableRow>
            ))}
            {Object.entries(p.figures).map(([k, v]) => (
              <TableRow key={k}>
                <TableCell>{FIGURE_LABEL[k] ?? k}</TableCell>
                <TableCell align="right" sx={k === "payable" ? { fontWeight: 600 } : undefined}>
                  {money(v)}
                </TableCell>
              </TableRow>
            ))}
            {p.calculation.proportionPct && (
              <TableRow>
                <TableCell>Proporción de prorrata</TableCell>
                <TableCell align="right">{p.calculation.proportionPct}%</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Conciliación con el borrador de TRIBU-CR
        </Typography>
        {p.reconciliation ? (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Casilla</TableCell>
                  <TableCell align="right">Despejo</TableCell>
                  <TableCell align="right">TRIBU-CR</TableCell>
                  <TableCell>Resultado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {p.reconciliation.fields.map((f) => (
                  <TableRow key={f.field}>
                    <TableCell>{FIGURE_LABEL[f.field] ?? f.field}</TableCell>
                    <TableCell align="right">{money(f.platform)}</TableCell>
                    <TableCell align="right">{money(f.draft)}</TableCell>
                    <TableCell>
                      {f.status === "MATCH" && "Coincide"}
                      {f.status === "EXPLAINED" && `Explicada: ${f.explainedBy.map((e) => e.note || CAUSE_LABEL[e.cause]).join("; ")}`}
                      {f.status === "UNEXPLAINED" && `Sin explicar: ${money(f.unexplained)}`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No hay borrador capturado. Cópielo en la pestaña Cálculo y guarde el cálculo.
          </Typography>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Comprobantes electrónicos incluidos ({p.receipts.length})
        </Typography>
        {p.receipts.length ? (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Clave</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Hacienda</TableCell>
                  <TableCell>Aceptación</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {p.receipts.map((r) => (
                  <TableRow key={`${r.clave}-${r.direction}`}>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}>{r.clave}</TableCell>
                    <TableCell>{formatDateEs(r.issueDate)}</TableCell>
                    <TableCell align="right">{r.currency === "CRC" ? money(r.total) : `${r.currency} ${Money.of(r.total).toFixed(2)}`}</TableCell>
                    <TableCell>{r.haciendaStatus === "ACCEPTED" ? "Aceptado" : r.haciendaStatus === "REJECTED" ? "Rechazado" : "Sin confirmar"}</TableCell>
                    <TableCell>{r.direction === "ISSUED" ? "Emitido" : (ACCEPTANCE_ES[r.acceptanceStatus ?? ""] ?? r.acceptanceStatus)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Ningún movimiento del periodo está ligado a un comprobante electrónico.
          </Typography>
        )}
      </Box>

      <Box sx={{ fontSize: 12, color: "text.secondary", wordBreak: "break-all" }}>
        <div>Motor: {p.engine} · Formato: {p.format}</div>
        <div>Huella de los datos de entrada (SHA-256): {p.inputSha256}</div>
        <div>Huella del paquete (SHA-256): {built.sha256}</div>
        {savedSha && savedSha !== built.sha256 && <div>El último paquete guardado tiene otra huella ({shortHash(savedSha)}): los datos cambiaron desde entonces.</div>}
      </Box>
      {extra}
    </Stack>
  );
}

/** Print only the package (window.print), with no new dependency. */
function PrintPortal({ children }: { children: ReactNode }) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const div = document.createElement("div");
    div.id = "filing-package-print";
    document.body.appendChild(div);
    setEl(div);
    return () => {
      document.body.removeChild(div);
    };
  }, []);
  return (
    <>
      <GlobalStyles
        styles={{
          "#filing-package-print": { display: "none" },
          "@media print": {
            "body > *:not(#filing-package-print)": { display: "none !important" },
            "#filing-package-print": { display: "block !important", padding: "12mm", color: "#000", background: "#fff" },
          },
        }}
      />
      {el && createPortal(children, el)}
    </>
  );
}

// ------------------------------------------------------------------ history

const ACCEPTANCE_ES: Record<string, string> = {
  PENDING: "Pendiente",
  ACCEPTED: "Aceptado",
  PARTIALLY_ACCEPTED: "Aceptado parcialmente",
  REJECTED: "Rechazado",
  NOT_REQUIRED: "No requiere",
};

export function FilingHistory({ obligationId }: { obligationId: string }) {
  const history = useFilingHistory(obligationId);
  if (history.isLoading) return <Typography variant="body2">Cargando historial…</Typography>;
  if (history.error) return <Alert severity="error">{(history.error as Error).message}</Alert>;
  const rows = history.data ?? [];
  if (!rows.length) return <Typography variant="body2" color="text.secondary">Aún no hay presentaciones registradas.</Typography>;
  return (
    <Stack spacing={1.5} divider={<Divider flexItem />}>
      {rows.map((f: FilingHistoryRow) => (
        <Box key={f.id}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="subtitle2">{f.version === 1 ? "Declaración original" : `Rectificativa ${f.version - 1}`}</Typography>
            {f.is_late ? <StatusChip tone="warning" icon="history" label="Tardía" /> : <StatusChip tone="success" icon="check_circle" label="En plazo" />}
            {f.package_sha256 ? <StatusChip tone="neutral" icon="verified" label={`Paquete ${shortHash(f.package_sha256)}`} /> : <StatusChip tone="neutral" icon="edit_document" label="Registro externo" />}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Presentada el {formatDateEs(crDateOf(new Date(f.filed_at)))}
            {f.amount_declared !== null ? ` · monto declarado ${formatCRC(Money.fromDb(f.amount_declared))}` : ""}
          </Typography>
          {f.reason && <Typography variant="body2">Motivo: {f.reason}</Typography>}
          {f.figures_diff && Object.keys(f.figures_diff).length > 0 && (
            <Table size="small" sx={{ mt: 0.5 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Cambio</TableCell>
                  <TableCell align="right">Antes</TableCell>
                  <TableCell align="right">Ahora</TableCell>
                  <TableCell align="right">Diferencia</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(f.figures_diff).map(([k, d]) => (
                  <TableRow key={k}>
                    <TableCell>{FIGURE_LABEL[k] ?? k}</TableCell>
                    <TableCell align="right">{money(d.before)}</TableCell>
                    <TableCell align="right">{money(d.after)}</TableCell>
                    <TableCell align="right">{d.diff === null ? "—" : `${Money.of(d.diff).isPositive() ? "+" : ""}${money(d.diff)}`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {f.version > 1 && f.figures_diff && Object.keys(f.figures_diff).length === 0 && (
            <Typography variant="caption" color="text.secondary">
              Sin cambios en las cifras frente a la versión anterior.
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
}

// -------------------------------------------------------------- main panel

export function FilingPackage({ row, onChanged }: { row: ObligationRow; onChanged: () => void }) {
  const qc = useQueryClient();
  const data = useFilingData(row);
  const me = useMyRole(row.organization_id);
  const comments = usePackageComments(row.id);
  const [explanations, setExplanations] = useState<UserExplanation[] | null>(null);
  const [built, setBuilt] = useState<BuiltFilingPackage | null>(null);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [date, setDate] = useState(crDateOf(new Date()));
  const [time, setTime] = useState("10:00");
  const [reason, setReason] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [expl, setExpl] = useState<{ field: string; cause: DiffCause; amount: string; note: string } | null>(null);

  // Restore the explanations of the last saved package once.
  useEffect(() => {
    if (data.data && explanations === null) setExplanations(data.data.latest?.explanations ?? []);
  }, [data.data, explanations]);

  useEffect(() => {
    if (!data.data || explanations === null) return;
    let live = true;
    buildFilingPackage({ ...data.data.base, userExplanations: explanations })
      .then((b) => live && (setBuilt(b), setBuildError(null)))
      .catch((e: Error) => live && setBuildError(e.message));
    return () => {
      live = false;
    };
  }, [data.data, explanations]);

  const role = me.data?.role ?? null;
  const latest = data.data?.latest ?? null;
  const filed = !["NOT_FILED", "NOT_APPLICABLE", "WAIVED"].includes(row.filing_status);
  const blocked = !built || !built.pkg.validation.ok;
  const savedIsCurrent = Boolean(built && latest && latest.package_sha256 === built.sha256);
  // Same data as the saved package (the evaluation day may differ): what a reviewer approves.
  const sameData = Boolean(built && latest && latest.input_sha256 === built.pkg.inputSha256);
  const approvedLabel = sameData && latest?.status === "APPROVED" ? `Aprobado por revisión${latest.approved_at ? ` el ${formatDateEs(crDateOf(new Date(latest.approved_at)))}` : ""}` : null;
  const unexplained = useMemo(() => built?.pkg.reconciliation?.fields.filter((f) => f.status === "UNEXPLAINED") ?? [], [built]);

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: filingKeys.data(row.id) }),
      qc.invalidateQueries({ queryKey: filingKeys.history(row.id) }),
      qc.invalidateQueries({ queryKey: filingKeys.comments(row.id) }),
    ]);
  };

  const act = async (fn: () => Promise<string | void>) => {
    setBusy(true);
    setMsg(null);
    try {
      const text = await fn();
      if (text) setMsg({ tone: "success", text });
      await refresh();
    } catch (e) {
      setMsg({ tone: "error", text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const save = async (): Promise<string> => {
    if (!built) throw new Error("El paquete aún no está listo");
    return must(await supabase.rpc("save_filing_package", { p_obligation_id: row.id, p_package_json: built.json })) as string;
  };

  const markFiled = () =>
    act(async () => {
      if (!built || !built.pkg.validation.ok) throw new Error("Resuelva los bloqueos antes de marcar como presentada");
      if (filed && !reason.trim()) throw new Error("Indique el motivo de la rectificativa");
      const id = await save();
      const receipt = file
        ? (
            await uploadDocument({
              organizationId: row.organization_id,
              taxpayerId: row.taxpayer_id,
              file,
              kind: "FILING_RECEIPT",
              periodKey: row.period_key,
              obligationId: row.id,
              title: `Comprobante de presentación ${row.form ?? row.obligation_code} ${row.period_key}${filed ? " (rectificativa)" : ""}`,
            })
          ).id
        : null;
      must(
        await supabase.rpc("file_with_package", {
          p_package_id: id,
          p_filed_at: crInstant(date, time).toISOString(),
          p_receipt_document_id: receipt,
          p_reason: filed ? reason.trim() : null,
        }),
      );
      onChanged();
      setReason("");
      setFile(null);
      return filed ? "Rectificativa registrada con la huella del paquete." : "Declaración marcada como presentada con la huella del paquete.";
    });

  const approve = () =>
    act(async () => {
      if (!latest || !sameData) throw new Error("Los datos cambiaron desde que se guardó el paquete");
      must(await supabase.rpc("approve_filing_package", { p_package_id: latest.id, p_note: note.trim() || null }));
      setNote("");
      return "Paquete aprobado.";
    });

  const addComment = () =>
    act(async () => {
      must(
        await supabase.from("filing_package_comments").insert({
          organization_id: row.organization_id,
          obligation_id: row.id,
          package_id: sameData ? latest!.id : null,
          author: me.data?.userId,
          body: comment.trim(),
        }),
      );
      setComment("");
    });

  const download = () => {
    if (!built) return;
    const url = URL.createObjectURL(new Blob([built.json], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `paquete-150-${row.period_key}-${built.sha256.slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const addExplanation = () => {
    if (!expl) return;
    const amount = parseAmount(expl.amount);
    if (!amount || !expl.note.trim()) return setMsg({ tone: "error", text: "La explicación necesita un monto y una nota" });
    setExplanations([...(explanations ?? []), { field: expl.field, cause: expl.cause, amount, note: expl.note.trim() }]);
    setExpl(null);
  };

  if (data.isLoading || me.isLoading) return <Typography variant="body2">Preparando el paquete…</Typography>;
  if (data.error) return <Alert severity="error">{(data.error as Error).message}</Alert>;
  if (buildError) return <Alert severity="error">{buildError}</Alert>;
  if (!built) return <Typography variant="body2">Preparando el paquete…</Typography>;

  const reviewerNote = latest?.approval_note ? <Typography variant="body2">Nota de revisión: {latest.approval_note}</Typography> : null;

  return (
    <Stack spacing={2}>
      <PrintPortal>
        <PackageBody
          built={built}
          row={row}
          savedSha={latest?.package_sha256 ?? null}
          approved={approvedLabel}
          extra={
            <Box sx={{ mt: 4, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
              <Box sx={{ borderTop: 1, pt: 1 }}>Preparado por</Box>
              <Box sx={{ borderTop: 1, pt: 1 }}>Revisado por</Box>
            </Box>
          }
        />
      </PrintPortal>

      <PackageBody built={built} row={row} savedSha={latest?.package_sha256 ?? null} approved={approvedLabel} extra={reviewerNote} />

      {can.write(role) && (explanations?.length ?? 0) > 0 && (
        <Box>
          <Typography variant="subtitle2">Explicaciones agregadas</Typography>
          {explanations!.map((e, n) => (
            <Stack key={n} direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {FIGURE_LABEL[e.field] ?? e.field}: {CAUSE_LABEL[e.cause]} · {money(e.amount)} · {e.note}
              </Typography>
              <Button size="small" onClick={() => setExplanations(explanations!.filter((_, i) => i !== n))}>
                Quitar
              </Button>
            </Stack>
          ))}
        </Box>
      )}

      {can.write(role) && unexplained.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Explicar una diferencia
          </Typography>
          {!expl ? (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
              {unexplained.map((f) => (
                <Button key={f.field} variant="outlined" size="small" onClick={() => setExpl({ field: f.field, cause: "OTHER", amount: f.unexplained, note: "" })}>
                  {FIGURE_LABEL[f.field] ?? f.field} ({money(f.unexplained)})
                </Button>
              ))}
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <Typography variant="caption" color="text.secondary">
                Monto con signo: Despejo menos TRIBU-CR. Queda en el paquete y en su huella.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField select size="small" label="Causa" value={expl.cause} onChange={(e) => setExpl({ ...expl, cause: e.target.value as DiffCause })} sx={{ minWidth: 220 }}>
                  {(Object.keys(CAUSE_LABEL) as DiffCause[]).map((c) => (
                    <MenuItem key={c} value={c}>
                      {CAUSE_LABEL[c]}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField size="small" label="Monto (₡)" value={expl.amount} onChange={(e) => setExpl({ ...expl, amount: e.target.value })} inputMode="decimal" />
              </Stack>
              <TextField size="small" label="Explicación" required value={expl.note} onChange={(e) => setExpl({ ...expl, note: e.target.value })} />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" size="small" onClick={addExplanation}>
                  Agregar explicación
                </Button>
                <Button size="small" onClick={() => setExpl(null)}>
                  Cancelar
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      )}

      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        <Button variant="outlined" startIcon={<Icon name="description" />} onClick={() => window.print()}>
          Imprimir lista
        </Button>
        <Button variant="outlined" startIcon={<Icon name="code" />} onClick={download}>
          Descargar JSON
        </Button>
        {can.write(role) && (
          <Button variant="outlined" disabled={busy || savedIsCurrent} onClick={() => act(async () => (await save(), "Paquete guardado para revisión."))}>
            {savedIsCurrent ? "Paquete guardado" : "Guardar paquete"}
          </Button>
        )}
      </Stack>

      {can.review(role) && (
        <Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Revisión
          </Typography>
          {!sameData ? (
            <Typography variant="body2" color="text.secondary">
              Para aprobar, el preparador debe guardar el paquete con los datos actuales.
            </Typography>
          ) : latest?.status === "APPROVED" ? (
            <Typography variant="body2">Este paquete ya está aprobado.</Typography>
          ) : (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField size="small" label="Nota de revisión (opcional)" value={note} onChange={(e) => setNote(e.target.value)} sx={{ flex: 1 }} />
              <Button variant="contained" disabled={busy || blocked || (latest?.blocking_count ?? 1) > 0} onClick={approve}>
                Aprobar paquete
              </Button>
            </Stack>
          )}
        </Box>
      )}

      {(can.write(role) || can.review(role) || (comments.data?.length ?? 0) > 0) && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Comentarios
          </Typography>
          {(comments.data ?? []).map((c) => (
            <Typography key={c.id} variant="body2" sx={{ mb: 0.5 }}>
              <Box component="span" sx={{ color: "text.secondary" }}>
                {formatDateEs(crDateOf(new Date(c.created_at)))}
                {c.author === me.data?.userId ? " · usted" : ""}:
              </Box>{" "}
              {c.body}
            </Typography>
          ))}
          {(can.write(role) || can.review(role)) && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField size="small" label="Agregar un comentario" value={comment} onChange={(e) => setComment(e.target.value)} sx={{ flex: 1 }} />
              <Button disabled={busy || !comment.trim()} onClick={addComment}>
                Comentar
              </Button>
            </Stack>
          )}
        </Box>
      )}

      {can.write(role) && (
        <Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            {filed ? "Registrar rectificativa" : "Marcar como presentada"}
          </Typography>
          {blocked && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              No se puede marcar como presentada mientras haya bloqueos en la lista de verificación.
            </Alert>
          )}
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={2}>
              <TextField label="Fecha" type="date" size="small" value={date} onChange={(e) => setDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
              <TextField label="Hora (Costa Rica)" type="time" size="small" value={time} onChange={(e) => setTime(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
            </Stack>
            {filed && <TextField size="small" label="Motivo de la rectificativa" required value={reason} onChange={(e) => setReason(e.target.value)} />}
            <Button component="label" variant="outlined" startIcon={<Icon name="upload_file" />} sx={{ justifyContent: "flex-start", alignSelf: "flex-start" }}>
              {file ? file.name : "Adjuntar comprobante de TRIBU-CR (opcional)"}
              <input hidden type="file" accept=".pdf,.png,.jpg,.jpeg,.xml" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </Button>
            <Typography variant="caption" color="text.secondary">
              Se guardará con la huella del paquete ({shortHash(built.sha256)}) y el monto declarado {money(built.pkg.figures.payable)}.
            </Typography>
            <Button variant="contained" disabled={busy || blocked || (filed && !reason.trim())} onClick={markFiled} sx={{ alignSelf: "flex-start" }}>
              {filed ? "Registrar rectificativa" : "Marcar como presentada"}
            </Button>
          </Stack>
        </Box>
      )}
      {!can.write(role) && !can.review(role) && (
        <Typography variant="body2" color="text.secondary">
          Tiene acceso de solo lectura a este paquete.
        </Typography>
      )}
      {msg && <Alert severity={msg.tone}>{msg.text}</Alert>}
    </Stack>
  );
}
